import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { submitUserIntentSurvey } from '@/lib/apiClient';
import { safeLocalStorage } from '@/lib/safeStorage';
import { useMutation } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

const STORAGE_KEY = 'expected_user_intent_survey_status';

// Routes whose primary CTA is pinned to the bottom-right (Send Now on the send
// flows) — exactly where this widget floats. The widget silently swallows taps
// on anything under it, so never render it over a page's primary action.
const SUPPRESSED_ROUTE_PREFIXES = ['/faxes/send', '/records-requests/new'];

export function UserIntentWidget() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [response, setResponse] = useState('');

  const submitMutation = useMutation({
    mutationKey: ['user-intent-survey-submit'],
    mutationFn: submitUserIntentSurvey,
    onSuccess: () => {
      toast.success('Thank you for your feedback!');
      safeLocalStorage.setItem(STORAGE_KEY, 'submitted');
      setIsVisible(false);
    },
    onError: error => {
      console.error('Failed to submit survey:', error);
      toast.error('Failed to submit feedback. Please try again.');
    },
  });

  useEffect(() => {
    // Check local storage to see if we should show the widget
    const status = safeLocalStorage.getItem(STORAGE_KEY);
    // Show if not submitted and not dismissed (or dismissed > 24h ago? For now just simple dismissed)
    // Actually, let's just use 'submitted' or 'dismissed'
    if (!status && user) {
      // Delay showing it slightly so it doesn't pop immediately on load?
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const handleDismiss = () => {
    setIsVisible(false);
    safeLocalStorage.setItem(STORAGE_KEY, 'dismissed');
  };

  const handleSubmit = () => {
    if (!response.trim()) return;
    submitMutation.mutate({ response });
  };

  if (!isVisible) return null;
  if (SUPPRESSED_ROUTE_PREFIXES.some(prefix => pathname.startsWith(prefix))) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 bg-background border border-border rounded-lg shadow-lg flex flex-col overflow-hidden animate-in slide-in-from-bottom-5 fade-in duration-300">
      {/* Header */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        className="bg-secondary p-3 flex justify-between items-center cursor-pointer border-b border-border"
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <span className="text-body-small-strong">Quick Question</span>
        <div className="flex items-center gap-2">
          <button
            onClick={e => {
              e.stopPropagation();
              handleDismiss();
            }}
            className="text-long-secondary hover:text-foreground p-1 rounded-md hover:bg-background/80 transition-colors"
            title="Don't show again"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 flex flex-col gap-3">
          <p className="text-body-small-normal">
            What are you trying to accomplish today?
          </p>
          <Textarea
            value={response}
            onChange={e => setResponse(e.target.value)}
            placeholder="I'm trying to..."
            maxLength={2000}
            className="min-h-[80px] text-base resize-none"
            autoFocus
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={!response.trim() || submitMutation.isPending}
            >
              {submitMutation.isPending ? 'Sending...' : 'Submit'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
